import React from 'react';
import { Link } from 'react-router-dom';

/**
 * ArticleCard Component - Enterprise Edition
 * 
 * CRITICAL REQUIREMENTS FROM MASTER BRIEF:
 * - Show ONLY: Image + Category Tag + Headline
 * - NO timestamps ("1 hour ago")
 * - NO author names
 * - NO long summaries
 * - Clean, professional, scannable
 * - Premium media brand aesthetic
 */

export const ArticleCard = ({ article, showImage = true, variant = 'grid' }) => {
  const getCategoryColor = (categoryName) => {
    const name = categoryName?.toLowerCase() || '';
    if (name.includes('healthcare')) return 'bg-[#00A86B]';
    if (name.includes('business')) return 'bg-[#0E2E59]';
    if (name.includes('education')) return 'bg-[#FF8C00]';
    if (name.includes('politics')) return 'bg-[#8B0000]';
    if (name.includes('innovation')) return 'bg-[#FFA500]';
    return 'bg-[#0E2E59]';
  };

  // Grid variant for homepage
  if (variant === 'grid' && showImage) {
    return (
      <article 
        className="group cursor-pointer bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-all duration-200" 
        data-testid="article-card"
      >
        <Link to={`/article/${article.slug}`} data-testid="article-link">
          {article.featured_image_url && (
            <div className="relative overflow-hidden aspect-[3/2]">
              <img
                src={article.featured_image_url}
                alt={article.featured_image_alt || article.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                loading="lazy"
                data-testid="article-thumbnail"
              />
            </div>
          )}
          
          <div className="p-5">
            {/* Category Badge - Small and Clean */}
            <div className="mb-3">
              <span 
                className={`inline-block ${getCategoryColor(article.category_name)} text-white text-xs font-bold uppercase tracking-wider px-3 py-1 rounded`}
                data-testid="card-category"
              >
                {article.category_name}
              </span>
            </div>
            
            {/* Headline - Bold and Prominent */}
            <h3 
              className="text-lg font-bold text-gray-900 leading-tight line-clamp-2 group-hover:text-[#0E2E59] transition-colors"
              data-testid="card-title"
            >
              {article.title}
            </h3>
            
            {/* Optional: Very brief deck - 1 line max */}
            {article.deck && (
              <p className="text-sm text-gray-600 mt-2 line-clamp-1" data-testid="card-deck">
                {article.deck}
              </p>
            )}
          </div>
        </Link>
      </article>
    );
  }

  // List variant for sidebar
  return (
    <article 
      className="border-b border-gray-200 pb-4 mb-4 last:border-0 last:pb-0 last:mb-0 hover:bg-gray-50 transition-colors px-2 py-3 rounded"
      data-testid="article-card"
    >
      <Link to={`/article/${article.slug}`} data-testid="article-link">
        <div className="flex items-start gap-1 mb-2">
          <span 
            className={`inline-block ${getCategoryColor(article.category_name)} text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded`}
            data-testid="card-category"
          >
            {article.category_name}
          </span>
        </div>
        <h3 
          className="text-base font-bold text-gray-900 leading-snug line-clamp-2 hover:text-[#0E2E59] transition-colors"
          data-testid="card-title"
        >
          {article.title}
        </h3>
      </Link>
    </article>
  );
};
